import { gql, TypedDocumentNode } from '@apollo/client';

export type UnsubscribePageValueFlags = Record<string, number>;

export type UnsubscribePageQueryNotificaitonSettingsFrequency = {
  name: string;
  value: number;
  text: string;
  description?: string;
};

export type UnsubscribePageQueryNotificationPreferencesEntity = {
  id: number;
  preferenceId: number;
  entityType: string;
  entityName: string;
  entityId: number;
};

export type UnsubscribePageQueryNotificationPreferencesEntityFlags = {
  [id: string]: UnsubscribePageQueryNotificationPreferencesEntity[];
};

export type UnsubscribePageQueryNotificationPreference = {
  id: string;
  attributes: {
    frequency: UnsubscribePageQueryNotificaitonSettingsFrequency;
    entities: UnsubscribePageQueryNotificationPreferencesEntity[];
    options: {
      description: string;
      human: string;
      tooltipText: string;
      frequencies: UnsubscribePageQueryNotificaitonSettingsFrequency[];
    };
  };
};

export type UnsubscribePageQuery = {
  unsubscribePage: {
    notificationPreferences: UnsubscribePageQueryNotificationPreference[];
  };
};

export type UnsubscribePageQueryVariables = {
  pathBuilder: (props: { '@rest': { path: string } }) => string;
};

export const UNSUBSCRIBE_PAGE_FRAGMENT = gql`
  fragment UnsubscribePageFragment on RestNotificationsPreferencesData {
    id
    attributes @type(name: "RestNotificationsPreferencesDataAttributes") {
      frequency @type(name: "RestNotificationsPreferencesDataAttributesFrequency") {
        name
        value
        text
        description
      }
      entities: notification_entities
        @type(name: "RestNotificationsPreferencesDataAttributesNotificationEntities") {
        id
        preferenceId: notification_preference_id
        entityId: entity_id
        entityType: entity_type
        entityName: entity_name
      }
      options @type(name: "RestNotificationsPreferencesDataAttributesOptions") {
        description
        human
        tooltipText: tooltip_text
        frequencies @type(name: "RestNotificationsPreferencesDataAttributesOptionsFrequencies") {
          name
          value
          text
          description
        }
      }
    }
  }
`;

export const UNSUBSCRIBE_PAGE_QUERY: TypedDocumentNode<
  UnsubscribePageQuery,
  UnsubscribePageQueryVariables
> = gql`
  query UnsubscribePageQuery($pathBuilder: Function) {
    unsubscribePage
      @rest(
        type: "RestNotificationsPreferences"
        endpoint: "public"
        path: "/v3.5/notifications"
        pathBuilder: $pathBuilder
      ) {
      notificationPreferences: data @type(name: "RestNotificationsPreferencesData") {
        ...UnsubscribePageFragment
      }
    }
  }
  ${UNSUBSCRIBE_PAGE_FRAGMENT}
`;

export type UpdateNotificationPreferencesMutationInputFlag = {
  id: string;
  type: 'notification_preferences';
  attributes: {
    frequency?: number;
    notification_entities_attributes?: {
      _destroy: 1;
      id: number;
      notification_preference_id: number;
      entity_id: number;
      entity_type: string;
      entity_name: string;
    }[];
  };
  hash?: string;
};

export type UpdateNotificationPreferencesMutationInput = {
  data: UpdateNotificationPreferencesMutationInputFlag[];
};

export const toUpdateNotificationPreferencesMutationInput = (
  valueFlags: UnsubscribePageValueFlags,
  entityFlags: UnsubscribePageQueryNotificationPreferencesEntityFlags,
  emailHash?: string
): UpdateNotificationPreferencesMutationInput => {
  const updatedFlags: UpdateNotificationPreferencesMutationInputFlag[] = Object.entries(
    valueFlags
  ).map(([id, frequency]) => ({
    id,
    type: 'notification_preferences',
    attributes: { frequency },
    ...(emailHash ? { hash: emailHash } : {})
  }));

  const updatedEntityFlags: UpdateNotificationPreferencesMutationInputFlag[] = Object.entries(
    entityFlags
  ).map(([id, entities]) => ({
    id,
    type: 'notification_preferences',
    attributes: {
      notification_entities_attributes: entities.map(entity => ({
        _destroy: 1,
        id: entity.id,
        notification_preference_id: entity.preferenceId,
        entity_id: entity.entityId,
        entity_type: entity.entityType,
        entity_name: entity.entityName
      })),
      ...(emailHash ? { hash: emailHash } : {})
    }
  }));

  return { data: [...updatedFlags, ...updatedEntityFlags] };
};

export const toUnsubscribeNotificationPreferencesMutationInput = (
  valueFlags: UnsubscribePageValueFlags,
  emailHash?: string
): UpdateNotificationPreferencesMutationInput => {
  const updatedFlags: UpdateNotificationPreferencesMutationInputFlag[] = Object.entries(
    valueFlags
  ).map(([id]) => ({
    id,
    type: 'notification_preferences',
    attributes: { frequency: 0 }, // Set `frequency` to `0` for all flags
    ...(emailHash ? { hash: emailHash } : {})
  }));

  return { data: updatedFlags };
};

export const UPDATE_NOTIFICATION_PREFERENCES_MUTATION: TypedDocumentNode<
  UpdateNotificationPreferencesMutation,
  UpdateNotificationPreferencesMutationVariables
> = gql`
  mutation UpdateNotificationPreferencesMutation(
    $input: UpdateNotificationPreferencesMutationInput
  ) {
    updateNotificationPreferences(input: $input)
      @rest(
        type: "RestUpdateNotificationPreferences"
        endpoint: "public"
        path: "/v3.5/notification_preferences"
        method: "PATCH"
        query: { email_hash: $emailHash }
      ) {
      notificationSettings: data @type(name: "RestNotificationsPreferencesData") {
        ...UnsubscribePageFragment
      }
    }
  }
  ${UNSUBSCRIBE_PAGE_FRAGMENT}
`;

export type UpdateNotificationPreferencesMutation = {
  notificationSettings: UnsubscribePageQueryNotificationPreference[];
};

export type UpdateNotificationPreferencesMutationVariables = {
  input: UpdateNotificationPreferencesMutationInput;
};
